import React, { useState } from "react";
import { useLanguage } from "@/hooks/useLanguage";

interface Lesson {
  id: number;
  title: string;
  duration: string;
  videoUrl: string;
  description: string;
  completed: boolean;
  language: string;
  resources: {
    title: string;
    type: string;
    url: string;
  }[];
}

interface Course {
  id: number;
  title: string;
  instructor: string;
  thumbnail: string;
  progress: number;
  totalDuration: string;
  lessons: Lesson[];
}

const demoCourse: Course = {
  id: 1,
  title: "البرمجة بلغة Python للمبتدئين",
  instructor: "أحمد محمد",
  thumbnail: "/assets/images/lessons/programming.jpg",
  progress: 45,
  totalDuration: "12 ساعة",
  lessons: [
    {
      id: 1,
      title: "مقدمة في Python",
      duration: "45 دقيقة",
      videoUrl: "https://example.com/video1.mp4",
      description: "تعرف على أساسيات لغة Python وبيئة التطوير المتكاملة.",
      completed: true,
      language: "ar",
      resources: [
        {
          title: "ملخص المحاضرة",
          type: "PDF",
          url: "/resources/lecture1.pdf",
        },
        {
          title: "الأكواد المستخدمة",
          type: "ZIP",
          url: "/resources/code1.zip",
        },
      ],
    },
    {
      id: 2,
      title: "المتغيرات وأنواع البيانات",
      duration: "60 دقيقة",
      videoUrl: "https://example.com/video2.mp4",
      description:
        "تعلم كيفية استخدام المتغيرات وأنواع البيانات المختلفة في Python.",
      completed: true,
      language: "ar",
      resources: [
        {
          title: "تمارين عملية",
          type: "PDF",
          url: "/resources/exercises2.pdf",
        },
      ],
    },
    {
      id: 3,
      title: "الحلقات التكرارية",
      duration: "55 دقيقة",
      videoUrl: "https://example.com/video3.mp4",
      description: "تعلم كيفية استخدام حلقات for و while في Python.",
      completed: false,
      language: "ar",
      resources: [
        {
          title: "أمثلة إضافية",
          type: "PDF",
          url: "/resources/examples3.pdf",
        },
      ],
    },
  ],
};

const EnrolledCourseView = () => {
  const { t } = useLanguage();
  const [currentLesson, setCurrentLesson] = useState<Lesson>(
    demoCourse.lessons[0]
  );
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-slate-900">
      {/* القائمة الجانبية للدروس */}
      <div className="w-full md:w-80 bg-slate-800 overflow-y-auto">
        <div className="p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white/90">
            {demoCourse.title}
          </h2>
          <div className="mt-2">
            <div className="flex items-center justify-between text-sm text-white/70 mb-1">
              <span>{t("courseProgress")}</span>
              <span>{demoCourse.progress}%</span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"
                style={{ width: `${demoCourse.progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-4">
          {demoCourse.lessons.map((lesson) => (
            <button
              key={lesson.id}
              onClick={() => setCurrentLesson(lesson)}
              className={`w-full text-right p-3 rounded-lg mb-2 transition-all ${
                currentLesson.id === lesson.id
                  ? "bg-blue-600/20 border border-blue-500/30"
                  : "hover:bg-slate-700/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`material-icons-outlined text-sm ${
                    lesson.completed ? "text-green-500" : "text-white/30"
                  }`}
                >
                  {lesson.completed ? "check_circle" : "radio_button_unchecked"}
                </span>
                <span className="flex-1 mx-3 text-white/90">
                  {lesson.title}
                </span>
                <span className="text-sm text-white/50">{lesson.duration}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* منطقة عرض المحتوى */}
      <div className="flex-1 overflow-y-auto">
        <div className="relative bg-black aspect-video">
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
            >
              <span className="material-icons-outlined text-4xl text-white">
                {isPlaying ? "pause" : "play_arrow"}
              </span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold text-white/90 mb-4">
              {currentLesson.title}
            </h1>
            <p className="text-white/70 mb-8">{currentLesson.description}</p>

            {/* الموارد والمرفقات */}
            <div className="bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white/90 mb-4">
                {t("lessonResources")}
              </h3>
              <div className="space-y-3">
                {currentLesson.resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-all"
                  >
                    <div className="flex items-center">
                      <span className="material-icons-outlined text-blue-400 ml-2">
                        {resource.type === "PDF" ? "description" : "folder_zip"}
                      </span>
                      <span className="text-white/90">{resource.title}</span>
                    </div>
                    <span className="text-sm text-white/50">
                      {resource.type}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* أزرار التنقل */}
            <div className="flex justify-between mt-8">
              <button
                disabled={currentLesson.id === 1}
                onClick={() => {
                  const prevLesson = demoCourse.lessons.find(
                    (l) => l.id === currentLesson.id - 1
                  );
                  if (prevLesson) setCurrentLesson(prevLesson);
                }}
                className="px-6 py-2 rounded-lg bg-slate-800 text-white/90 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-all"
              >
                {t("prevLesson")}
              </button>
              <button
                disabled={currentLesson.id === demoCourse.lessons.length}
                onClick={() => {
                  const nextLesson = demoCourse.lessons.find(
                    (l) => l.id === currentLesson.id + 1
                  );
                  if (nextLesson) setCurrentLesson(nextLesson);
                }}
                className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("nextLesson")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrolledCourseView;
